import React, { useEffect, useState, useRef, useContext, createContext } from "react";
import Link from "@qlik/qlik/src/components/Link";
import Cta from "@qlik/qlik/src/components/Cta";
import { getVidyardURL, addParamToURL, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import { POPUP } from "@qlik/qlik/src/lib/constants/cta-constants";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { useHover } from "react-use";
import { useMediaQuery } from "react-responsive";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import * as styles from "./styles.module.css";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const PlayVideoContext = createContext(null);

// Vidyard link component.
const VideoPopupWrapper = ({ className, video, children }) => {
    const vidyardVideoUrl = getVidyardURL(video);
    const vidyardVideoUrlWithAutoplay = addParamToURL(vidyardVideoUrl, "autoplay", "1");

    // Video ref, used to play Vidyard video with main CTA button.
    const thumbnailVideoRef = useContext(PlayVideoContext);

    return (
        <a
            ref={thumbnailVideoRef}
            className={className}
            data-found-player="0"
            data-type="iframe"
            data-fancybox=""
            data-src={vidyardVideoUrlWithAutoplay}
            href="#"
            data-iframe="{ preload: false }"
        >
            {children}
        </a>
    );
};

// Card component used for both mobile and desktop.
const BentoCard = ({ item }) => {
    // Use different tags depending on what type of content is being shown (link/video/image).
    const CardTag = (item.link ? Link : false) || (item.videoModal ? VideoPopupWrapper : false) || "div";

    // Add a class to toggle z-index for hovered cards (without a delayed hover the box shadow goes behind
    // other tiles as soon as hover is removed).
    const [delayedIsHovered, setDelayedIsHovered] = useState(false);

    // Content is wrapped in this sub-component for use with useHover hook (see below).
    const element = (hovered) => {
        if (hovered && !delayedIsHovered) setDelayedIsHovered(true);
        if (!hovered && delayedIsHovered) setTimeout(() => setDelayedIsHovered(false), 300);

        // Media query used for playing/pausing the thumbnail video.
        const isDesktop = useMediaQuery({ query: "(min-width: 992px)" }, undefined);

        // Don't show the thumbnail video until the page has loaded (allowing the thumbnail image to show through).
        const [thumbnailVideoVisible, setThumbnailVideoVisible] = useState(false);
        const [videoLoaded, setVideoLoaded] = useState(false);

        // Create a video ref for playing/pausing.
        const videoRef = useRef(null);

        useEffect(() => {
            // Show the video on page load.
            setThumbnailVideoVisible(true);

            // If the use prefers reduced motion, don't play the video.
            const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

            const video = videoRef.current;
            if (video) {
                // Play the video on desktop (for users that don't prefer reduced motion), pause on mobile.
                if (!prefersReducedMotion && isDesktop) {
                    video.play();
                } else {
                    video?.pause();
                }
            }

            // Pause the video on unmount.
            return () => {
                if (videoRef?.current) {
                    videoRef?.current?.pause();
                }
            };
        }, [isDesktop, videoLoaded]);

        return (
            <CardTag
                video={item.videoModal ?? null}
                to={item.link ?? null}
                className={`${cx("bentoCard", item.size, item.color, {
                    hasLink: item.link,
                    hasVideo: item.videoModal,
                    hovered: delayedIsHovered,
                })} block h-[342px] w-70 rounded-[14px] overflow-hidden`}
            >
                {item.topText || item.topSubText ? (
                    <div className="absolute left-[20px] top-[19px] text-[15.5px] text-white">
                        {item.topText && <div>{item.topText}</div>}
                        {item.topSubText && <div className="text-[10.8px]">{item.topSubText}</div>}
                    </div>
                ) : null}

                <picture>
                    <source media="(min-width: 992px)" srcSet={item.image} />
                    <img
                        className="w-full h-full object-cover"
                        src={item.imageMobile ?? item.image}
                        alt={item.imageAlt}
                    />
                </picture>

                {item.videoThumbnail && thumbnailVideoVisible && (
                    <video
                        ref={videoRef}
                        loop={true}
                        muted={true}
                        className="hidden lg:block w-full h-full object-cover absolute top-0"
                        src={item.videoThumbnail}
                        onCanPlayThrough={() => setTimeout(() => setVideoLoaded(true))}
                    />
                )}
                {item.bottomText && (
                    <div className="absolute z-20 w-2/3 left-[20px] bottom-[22px] text-[16px] text-white leading-tight">
                        {item.bottomText && <div>{item.bottomText}</div>}
                    </div>
                )}
                {item.link && !item.bottomText && (
                    <div
                        className={`${cx(
                            "linkText"
                        )} z-20 absolute w-2/3 left-[20px] bottom-[22px] text-[16px] text-white leading-tight`}
                    >
                        Read more <i className="arrow-white inline-block ml-1" />
                    </div>
                )}
                {item.videoModal && (
                    <div
                        style={{
                            backgroundImage: `url("${item.videoButtonImage}")`,
                        }}
                        className={`z-20 h-[42px] w-[42px] absolute w-1/3 right-[20px] bottom-[22px]`}
                    />
                )}
            </CardTag>
        );
    };

    const [hoverable] = useHover(element);
    return hoverable;
};

// Slider for mobile.
const MobileSlider = ({ bento, sliderLoaded }) => (
    <div style={{ willChange: "opacity" }} className={cx("slider", { sliderLoaded }, "w-[280px]")}>
        <Swiper slidesPerView={"auto"} spaceBetween={15}>
            {bento.map((item, i) => (
                <SwiperSlide key={i}>
                    <BentoCard item={item} />
                </SwiperSlide>
            ))}
        </Swiper>
    </div>
);

// "Bento" for desktop.
const DesktopBento = ({ bento }) => (
    <div
        className={`${cx(
            "bento"
        )} hidden absolute top-[15px] right-[15px] xxl:right-[-102px] lg:flex flex-wrap w-[720px] h-[700px] flex-col columns-3 gap-[15px]`}
    >
        {bento.map((item, i) => (
            <BentoCard key={i} item={item} />
        ))}
    </div>
);

// Hero Bento component.
const HeroBento = ({ data }) => {
    const parsedData = data?.data?.internal?.content ? JSON.parse(data?.data?.internal?.content) : {};

    const { title, additionalConfiguration, customId, eyebrow, description, cta, bento } = parsedData;

    // Get the class name for section padding
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "py-10 lg:py-0");

    const thumbnailVideoRef = useRef(null);

    //
    const [sliderLoaded, setSliderLoaded] = useState(false);
    useEffect(() => {
        setSliderLoaded(true);
    }, []);

    // Render HeroBento component
    return (
        <section className={`${sectionSpacingClassName} overflow-hidden lg:overflow-visible relative`} id={customId}>
            <div className="container lg:flex xxl:relative">
                <div className={`${cx("content")} lg:max-w-[410px] lg:h-[655px] lg:pt-[120px]`}>
                    {eyebrow && (
                        <div className="eyebrow text-ocean uppercase tracking-wide4 leading-tight mb-3 text-[14px]">
                            {eyebrow}
                        </div>
                    )}
                    {title && (
                        <h1
                            className="text-with-color mb-4 title-1 text-[60px] lg:text-[72px] xxl:text-[94px]"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                        />
                    )}
                    {description && <p className="font-light mb-8 text-gray-dark text-[18px]">{description}</p>}
                    {cta && (
                        <Cta
                            data={{ externalUrl: cta.video, linkBehavior: POPUP }}
                            className="btn-primary-large mb-8 cursor-pointer"
                        >
                            {cta.text}
                        </Cta>
                    )}
                </div>

                <PlayVideoContext.Provider value={thumbnailVideoRef}>
                    <MobileSlider bento={bento} sliderLoaded={sliderLoaded} />
                    <DesktopBento bento={bento} />
                </PlayVideoContext.Provider>
            </div>
        </section>
    );
};

export default HeroBento;
